/**
 * JSON-LD builders.
 *
 * Two levels, deliberately separated:
 *
 *   Platform level — LookBel is a marketplace/`Organization` and a `WebSite`.
 *   It is NEVER represented as a physical `LocalBusiness` in a city it does not
 *   operate a premises in. That is the single most common (and most penalised)
 *   marketplace structured-data mistake.
 *
 *   Provider level — an individual stylist IS the local entity. They get
 *   `Person` (+ `BeautySalon` when they trade under a real business name),
 *   with a locality/region-only `PostalAddress` so we never publish a private
 *   street address.
 *
 * Every optional property is omitted unless the supplied model carries real
 * data for it. No placeholder ratings, no invented review counts, no fake
 * prices — an absent field is simply absent from the graph.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const { canonicalUrl, getSiteOrigin, ROUTES } = require('./urls');
const { resolveState } = require('./states');
const { resolveServiceCategory } = require('./taxonomy');
const { SITE_NAME } = require('./metadata');

/**
 * Drop undefined / null / '' / [] / {} so the emitted graph only contains
 * properties we can actually stand behind.
 * @param {Record<string, unknown>} obj
 */
function compact(obj) {
  /** @type {Record<string, unknown>} */
  const out = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      return;
    }
    out[key] = value;
  });
  return out;
}

function positiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Serialize for embedding in a <script type="application/ld+json"> block.
 * `<` is escaped so a hostile bio string can never close the script element.
 * @param {unknown} data
 * @returns {string}
 */
function serializeJsonLd(data) {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

/* ------------------------------------------------------------------ *
 * Platform level
 * ------------------------------------------------------------------ */

/**
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 */
function organizationSchema(ctx) {
  const c = ctx || {};
  const origin = getSiteOrigin(c.environment, c.siteUrl);
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${origin}/#organization`,
    name: SITE_NAME,
    url: `${origin}/`,
    logo: `${origin}/lb-logo512.png`,
    description:
      'LookBel is a United States marketplace where clients discover and book independent beauty and wellness professionals.',
  };
}

/**
 * WebSite + SearchAction. The search target is the public stylist directory,
 * which really does accept a `q` parameter.
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 */
function webSiteSchema(ctx) {
  const c = ctx || {};
  const origin = getSiteOrigin(c.environment, c.siteUrl);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    name: SITE_NAME,
    url: `${origin}/`,
    publisher: { '@id': `${origin}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin}/stylists?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * @param {Array<{ name: string, path: string }>} trail
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 */
function breadcrumbSchema(trail, ctx) {
  const c = ctx || {};
  const items = (Array.isArray(trail) ? trail : []).filter((i) => i && i.name && i.path);
  if (items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path, c),
    })),
  };
}

/**
 * ItemList of the stylists actually rendered on the page. Only entries with a
 * real name and a real public profile URL are included, so the list can never
 * claim more supply than the page shows.
 *
 * @param {Array<{ displayName?: string, seoSlug?: string }>} stylists
 * @param {{ name?: string, environment?: string, siteUrl?: string }} [ctx]
 */
function stylistItemListSchema(stylists, ctx) {
  const c = ctx || {};
  const listed = (Array.isArray(stylists) ? stylists : []).filter(
    (s) => s && s.displayName && s.seoSlug
  );
  if (listed.length === 0) return null;
  return compact({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: c.name,
    numberOfItems: listed.length,
    itemListElement: listed.map((stylist, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: stylist.displayName,
      url: canonicalUrl(ROUTES.stylistProfile(stylist.seoSlug), c),
    })),
  });
}

/**
 * `Service` node for a national service hub page.
 * @param {{ serviceCategory: string, priceMin?: number, priceMax?: number }} model
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 */
function serviceCategorySchema(model, ctx) {
  const m = model || {};
  const c = ctx || {};
  const category = resolveServiceCategory(m.serviceCategory);
  if (!category) return null;
  const origin = getSiteOrigin(c.environment, c.siteUrl);
  const lo = positiveNumber(m.priceMin);
  const hi = positiveNumber(m.priceMax);

  return compact({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: category.label,
    serviceType: category.serviceNoun,
    url: canonicalUrl(ROUTES.service(category.slug), c),
    provider: { '@id': `${origin}/#organization` },
    areaServed: { '@type': 'Country', name: 'United States' },
    offers:
      lo && hi
        ? compact({
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: Math.round(Math.min(lo, hi)),
            highPrice: Math.round(Math.max(lo, hi)),
          })
        : undefined,
  });
}

/* ------------------------------------------------------------------ *
 * Provider level
 * ------------------------------------------------------------------ */

/**
 * Locality/region-only address. We intentionally never emit `streetAddress`
 * for an individual provider — many work from home, and their exact address is
 * private data that must not appear on a public page.
 *
 * @param {{ city?: string, state?: string }} model
 */
function publicPostalAddress(model) {
  const state = resolveState(model && model.state);
  const city = model && model.city ? String(model.city).trim() : '';
  if (!city && !state) return null;
  return compact({
    '@type': 'PostalAddress',
    addressLocality: city || undefined,
    addressRegion: state ? state.code : undefined,
    addressCountry: 'US',
  });
}

/**
 * Only emitted when the marketplace has genuinely accumulated published
 * ratings for this provider AND those ratings are visible on the page.
 *
 * @param {{ ratingAverage?: number, ratingCount?: number }} model
 */
function aggregateRatingNode(model) {
  const avg = positiveNumber(model && model.ratingAverage);
  const count = positiveNumber(model && model.ratingCount);
  if (!avg || !count) return null;
  return {
    '@type': 'AggregateRating',
    ratingValue: Number(avg.toFixed(1)),
    reviewCount: Math.floor(count),
    bestRating: 5,
    worstRating: 1,
  };
}

/**
 * @param {Array<{ authorName?: string, rating?: number, body?: string, publishedAt?: string }>} reviews
 */
function reviewNodes(reviews) {
  return (Array.isArray(reviews) ? reviews : [])
    .filter((r) => r && positiveNumber(r.rating) && r.authorName)
    .map((r) =>
      compact({
        '@type': 'Review',
        author: { '@type': 'Person', name: String(r.authorName) },
        datePublished: r.publishedAt || undefined,
        reviewBody: r.body || undefined,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: Number(r.rating),
          bestRating: 5,
          worstRating: 1,
        },
      })
    );
}

/**
 * @param {Array<{ name?: string, priceMin?: number, priceMax?: number }>} services
 */
function offerNodes(services) {
  return (Array.isArray(services) ? services : [])
    .filter((s) => s && s.name)
    .map((s) => {
      const lo = positiveNumber(s.priceMin);
      const hi = positiveNumber(s.priceMax);
      return compact({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: String(s.name) },
        priceCurrency: lo || hi ? 'USD' : undefined,
        priceSpecification:
          lo && hi
            ? {
                '@type': 'PriceSpecification',
                minPrice: Math.round(Math.min(lo, hi)),
                maxPrice: Math.round(Math.max(lo, hi)),
                priceCurrency: 'USD',
              }
            : undefined,
      });
    });
}

/**
 * Provider graph for a public stylist profile.
 *
 * @param {Object} model public profile projection (no email / phone / street address)
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 * @returns {Object | null} a schema.org @graph document
 */
function stylistProfileSchema(model, ctx) {
  const m = model || {};
  const c = ctx || {};
  if (!m.seoSlug || !m.displayName) return null;

  const profileUrl = canonicalUrl(ROUTES.stylistProfile(m.seoSlug), c);
  const address = publicPostalAddress(m);
  const rating = aggregateRatingNode(m);
  const offers = offerNodes(m.services);
  const reviews = reviewNodes(m.reviews);
  const category = resolveServiceCategory(m.primaryCategory);
  const lo = positiveNumber(m.priceMin);
  const hi = positiveNumber(m.priceMax);

  const person = compact({
    '@type': 'Person',
    '@id': `${profileUrl}#person`,
    name: String(m.displayName),
    url: profileUrl,
    image: m.profilePhotoUrl || undefined,
    description: m.shortBio || m.fullBio || undefined,
    jobTitle: category ? category.providerNoun.replace(/s$/, '') : undefined,
    address: address || undefined,
    makesOffer: offers.length ? offers : undefined,
  });

  /** @type {Array<Record<string, unknown>>} */
  const graph = [person];

  // Only claim a local business entity when the provider actually trades under
  // a business name — otherwise the Person node is the honest representation.
  if (m.businessName) {
    graph.push(
      compact({
        '@type': 'BeautySalon',
        '@id': `${profileUrl}#business`,
        name: String(m.businessName),
        url: profileUrl,
        image: m.profilePhotoUrl || undefined,
        description: m.shortBio || m.fullBio || undefined,
        address: address || undefined,
        areaServed: address
          ? compact({ '@type': 'City', name: m.city || undefined })
          : undefined,
        employee: { '@id': `${profileUrl}#person` },
        priceRange:
          lo && hi ? `$${Math.round(Math.min(lo, hi))}-$${Math.round(Math.max(lo, hi))}` : undefined,
        aggregateRating: rating || undefined,
        review: reviews.length ? reviews : undefined,
        makesOffer: offers.length ? offers : undefined,
      })
    );
  } else if (rating || reviews.length) {
    // Ratings/reviews belong on the entity being reviewed; with no business
    // entity they attach to the Person.
    person.aggregateRating = rating || undefined;
    if (reviews.length) person.review = reviews;
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

module.exports = {
  compact,
  serializeJsonLd,
  organizationSchema,
  webSiteSchema,
  breadcrumbSchema,
  stylistItemListSchema,
  serviceCategorySchema,
  publicPostalAddress,
  aggregateRatingNode,
  stylistProfileSchema,
};
